import { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18n-lite'

import {
  DocumentListCard,
  Field,
  FilteractionObject
} from '@yes.technology/react-toolkit'

import useObjectClassFilteractions from 'filteraction/shared/hooks/useObjectClassFilteractions'

import { FieldSettings } from 'shared/types'

import SectionDivider from 'site/Renderer/components/SectionDivider'

import { useSiteState } from 'siteState/shared'

import { useSituations } from 'situation/shared'

import { FilterFieldsDisplayOptions } from '../../FilterFields'
import { FilterActionSetup, FilterButton } from '../../FilterFields.styles'

import { formatFields } from '../../shared'

import { FieldValues, FilterAreaLayout, GroupedFieldValues } from '../../types'

import Fields from '../Fields/Fields'
import FilteractionSelection from '../FilteractionSelection/FilteractionSelection'
import getOnSelectFilteraction from '../FilteractionSelection/getOnSelectFilteraction'
import getFilterStateFromFilteraction from '../FilteractionSelection/getQueryDataFromFilteraction'
import GroupControls from '../GroupControls/GroupControls'

type FieldsProps = {
  filterAreaLayout?: FilterAreaLayout
  sections: Field[][]
  groupedFieldValues: GroupedFieldValues
  initialFieldValues: FieldValues
  composition: string
  title?: string
  handleChange?: (value: GroupedFieldValues) => void
  handleClear?: () => void
  handleComposition: (composition: string) => void
  uuidCurrentSitemodelObjectclass?: string
  navigationSliderBackgroundColor?: string
  navigationSliderColor?: string
  defaultExecutedFilteraction?: string
  executeFilter: (fieldValues: GroupedFieldValues, composition: string) => void
  elementUuid: string
  fieldSettings?: FieldSettings
  filteractionOptions?: FilteractionObject[]
}

const Section = ({
  filterAreaLayout,
  sections,
  initialFieldValues,
  groupedFieldValues,
  title,
  handleChange,
  handleClear,
  handleComposition,
  uuidCurrentSitemodelObjectclass = '',
  navigationSliderBackgroundColor,
  navigationSliderColor,
  defaultExecutedFilteraction,
  executeFilter,
  elementUuid,
  fieldSettings,
  filteractionOptions
}: FieldsProps) => {
  const { t } = useTranslation()

  const [sectionPosition, setSectionPosition] = useState<number>(1)

  const sectionsLength = sections.length

  const currentFields = formatFields(sections[sectionPosition - 1])

  const [groupPosition, setGroupPosition] = useState(1)

  const groupsLength = Object.keys(groupedFieldValues).length

  if (groupsLength < groupPosition) {
    setGroupPosition(1)
  }

  const [filteractionValue, setFilteractionValue] = useState('')

  const filteractions = useObjectClassFilteractions({
    objectclassUuid: uuidCurrentSitemodelObjectclass,
    filteractionOptions
  })

  const situations = useSituations()

  const onSelectFilteraction = getOnSelectFilteraction({
    initialFieldValues,
    sections,
    situations,
    handleChange,
    handleClear,
    handleComposition
  })

  const fieldValues = groupedFieldValues[groupPosition]

  const handleChangeFieldValues = (value: FieldValues) => {
    handleChange?.({ [groupPosition]: value })
  }

  const handleClearCurrentGroup = () =>
    handleChangeFieldValues(initialFieldValues)

  const handleClearAll = () => {
    handleClear?.()

    setFilteractionValue('')
  }

  useEffect(() => {
    if (
      defaultExecutedFilteraction &&
      filteractions.length > 0 &&
      situations.length > 0
    ) {
      const filteraction = filteractions.find(
        (filteraction) => filteraction.uuid === defaultExecutedFilteraction
      )

      if (filteraction) {
        const { queryData, composition } = getFilterStateFromFilteraction({
          initialFieldValues,
          sections,
          situations,
          filteraction
        })

        executeFilter(queryData, composition)

        setFilteractionValue(filteraction.des)

        handleChange?.(queryData)

        handleComposition(composition)
      }
    }
  }, [
    defaultExecutedFilteraction,
    filteractions,
    situations,
    executeFilter,
    sections,
    initialFieldValues,
    handleComposition,
    handleChange
  ])

  const hideFiltersAfterFilteraction =
    !!filterAreaLayout?.filteractions?.hide_filters_after_filteraction

  const shouldHideFilters = hideFiltersAfterFilteraction && !!filteractionValue

  const sectionLayout = filterAreaLayout?.filter_sections?.[sectionPosition - 1]

  const [displayOptions] = useSiteState<FilterFieldsDisplayOptions>(
    'filter-display-options'
  )

  const displayOptionsElement = displayOptions?.[elementUuid]

  const interdependentFilteringConfig =
    filterAreaLayout?.interdependent_filtering

  return (
    <SectionDivider
      title={`${title} | ${t('filter-fields.filters')}`}
      backgroundColor='#004099'
      isVisible={!displayOptionsElement?.hideMainCollapsibleContainer}
    >
      <DocumentListCard style={{ padding: 8 }}>
        <FilterActionSetup>
          <FilterButton
            type='submit'
            className='flex-grow-1 flex-md-grow-0 fw-semibold'
            heightClass='regular'
          >
            {t('filter-fields.filter')}
          </FilterButton>
          <FilterButton
            variant='secondary'
            onClick={handleClearAll}
            className='flex-grow-1 flex-md-grow-0 fw-semibold'
            heightClass='regular'
          >
            {t('filter-fields.clean')}
          </FilterButton>
          {!displayOptionsElement?.hideFilteraction && (
            <FilteractionSelection
              value={filteractionValue}
              setValue={setFilteractionValue}
              {...{ filteractions, onSelectFilteraction }}
            />
          )}
        </FilterActionSetup>
        <GroupControls
          {...{
            sectionPosition,
            sectionsLength,
            groupPosition,
            groupsLength,
            setGroupPosition,
            setSectionPosition,
            handleClearCurrentGroup,
            navigationSliderBackgroundColor,
            navigationSliderColor,
            shouldHideFilters,
            elementUuid
          }}
        >
          <div className='px-3'>
            {currentFields.length && !shouldHideFilters && (
              <Fields
                sectionLayout={sectionLayout}
                fields={currentFields}
                handleChange={handleChangeFieldValues}
                {...{
                  fieldValues,
                  uuidCurrentSitemodelObjectclass,
                  groupedFieldValues,
                  interdependentFilteringConfig,
                  fieldSettings,
                  groupPosition
                }}
              />
            )}
          </div>
        </GroupControls>
      </DocumentListCard>
    </SectionDivider>
  )
}

export default Section
